import React from 'react';
import { motion } from 'framer-motion';
import { FiCode, FiLayers, FiFileText, FiTrendingUp } from 'react-icons/fi';
import { IconType } from 'react-icons';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

// UseCaseCard Component
const UseCaseCard = ({ title, icon, color, features, description }) => {
  const colorMap = {
    blue: "from-blue-600 to-blue-900",
    purple: "from-purple-600 to-purple-900",
    cyan: "from-cyan-500 to-cyan-800",
    amber: "from-amber-500 to-amber-800"
  };
  
  const gradientClass = colorMap[color] || colorMap.blue;
  
  // Fix: Ensure icon is properly rendered as a component
  const IconComponent = typeof icon === 'function' ? icon : () => icon;
  
  return (
    <motion.div 
      className="relative group"
      variants={itemVariants}
      whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)" }}
    >
      {/* Dynamic Glow Layer */}
      <div className={`absolute -inset-1 bg-gradient-to-r ${gradientClass} rounded-xl blur-xl opacity-30 group-hover:opacity-70 transition-opacity duration-700`} />
      
      <div className={`relative p-6 rounded-xl border border-white/10 bg-slate-950/80 backdrop-blur-md transition-all duration-300 h-full overflow-hidden`}>
        {/* Specular Layer */}
        <div className="specular-shine absolute inset-0 opacity-30 pointer-events-none" />
        
        {/* Header */}
        <div className="flex items-start mb-4">
          <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${gradientClass} flex items-center justify-center mr-4 shadow-lg`}>
            {React.isValidElement(icon) ? icon : <IconComponent />}
          </div>
          <h3 className="text-xl font-black tracking-tight text-white uppercase">
            {title}
          </h3>
        </div>
        
        {/* Description */}
        <p className="text-sm text-gray-300 mb-6 font-medium leading-relaxed">
          {description}
        </p>
        
        {/* Features List */}
        <ul className="space-y-2 mb-4">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center text-sm">
              <span className={`w-1.5 h-1.5 rounded-full bg-${color === 'cyan' ? 'cyan' : color}-500 mr-2`}></span>
              <span className="text-gray-300 font-mono text-xs">{feature}</span>
            </li>
          ))}
        </ul>
        
        {/* Action Button */}
        <div className="mt-auto pt-4 flex justify-end">
          <div className="flex items-center gap-2 group/btn cursor-pointer">
            <span className="text-xs font-bold uppercase tracking-wider text-white group-hover/btn:text-brand-cyan transition-colors">
              Explore
            </span>
            <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center group-hover/btn:bg-cyan-500 transition-colors">
              <svg className="w-3 h-3 text-white transform group-hover/btn:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface FeaturesSectionProps {
  darkMode: boolean;
  title?: string;
  useCases?: {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    features: string[];
  }[];
  inView?: boolean;
}

export const FeaturesSection: React.FC<FeaturesSectionProps> = ({ 
  darkMode,
  title = "Powerful Use Cases",
  useCases = [
    {
      title: "Software Development",
      icon: <FiCode className="text-3xl" />,
      color: "blue",
      features: [
        "System architecture planning",
        "Code generation and review",
        "Test case development",
        "Technical documentation"
      ],
      description: "Accelerate your development process with specialized personas for architecture design, code generation, testing, and documentation."
    },
    {
      title: "Product Design",
      icon: <FiLayers className="text-3xl" />,
      color: "purple",
      features: [
        "User research and personas",
        "UX/UI design concepts",
        "Interactive prototypes",
        "Usability testing plans"
      ],
      description: "Streamline your product design workflow with personas specialized in user research, UX design, prototyping, and user testing."
    },
    {
      title: "Content Creation",
      icon: <FiFileText className="text-3xl" />,
      color: "cyan",
      features: [
        "Blog posts and articles",
        "Marketing copy",
        "Technical documentation",
        "Social media content"
      ],
      description: "Create high-quality content with personas specialized in research, writing, editing, and SEO optimization."
    },
    {
      title: "Business Strategy",
      icon: <FiTrendingUp className="text-3xl" />,
      color: "amber",
      features: [
        "Market research and analysis",
        "Competitive landscape assessment",
        "Business model development",
        "Go-to-market strategies"
      ],
      description: "Develop comprehensive business strategies with personas specialized in market analysis, financial planning, and strategic development."
    }
  ],
  inView = true
}) => {
  return (
    <div className={`relative py-16 px-4 overflow-hidden ${darkMode ? 'bg-surface-deep' : 'bg-gray-100'}`}>
      {/* Matrix Grid Background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-70"></div>
      
      {/* Noise Overlay */}
      <div className="noise-overlay"></div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.h2 
          className={`text-3xl font-black tracking-tight uppercase mb-12 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-brand-cyan">Powerful</span> {title}
        </motion.h2>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {useCases.map((useCase, index) => (
            <UseCaseCard 
              key={index}
              title={useCase.title}
              icon={useCase.icon}
              color={useCase.color}
              features={useCase.features}
              description={useCase.description}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};
